import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Container,
  Row,
  Card,
  CardBody,
  CardHeader,
  Col,
  Spinner,
  Input,
  InputGroup,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { toast } from "react-toastify";
import { APIClient } from "../../../helpers/api_helper";
import AwesomePagination from "../../../Components/Common/AwesomePagination";
import moment from "moment";
import FeatherIcon from "feather-icons-react";

const apiClient = new APIClient();

const Vouchers = ({ isLojista, isCliente }) => {
  const [loading, setLoading] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(true);
  const [termo, setTermo] = useState("");
  const [inputTermo, setInputTermo] = useState("");
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalItens, setTotalItens] = useState(0);
  const [vouchers, setVouchers] = useState([]);

  const { userId } = useParams();
  const cliente_id = userId || null;

  const user = JSON.parse(localStorage.getItem("authUser"));

  const formatCurrency = (value) => {
    if (!value) {
      return "";
    }
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const getVouchers = useCallback(async () => {
    setLoading(true);
    
    // Se não há cliente_id específico e o usuário não é admin, usar o próprio ID
    const targetClienteId = cliente_id || (isCliente ? user.data._id : null);
    
    if (!targetClienteId && !user.data.role === "Admin") {
      toast.error("ID do cliente é obrigatório");
      setLoading(false);
      return;
    }

    const response = await apiClient.post(`/vouchers/clientes/busca`, {
      cliente_id: targetClienteId,
      termo: termo,
      pagina: pagina,
      itens_pagina: 20,
      order_by: "created_at",
      order: "desc"
    });

    if (response.sucesso) {
      setVouchers(response.dados.data);
      setTotalPaginas(response.dados.last_page || Math.ceil(response.dados.total / 20));
      setTotalItens(response.dados.total);
    } else {
      toast.error(response.mensagem);
    }
    setLoading(false);
  }, [cliente_id, pagina, termo, isCliente, user.data._id, user.data.role]);

  useEffect(() => {
    if (shouldFetch) {
      const fetchData = async () => {
        await getVouchers();
        setShouldFetch(false);
      };
      fetchData();
    }
  }, [shouldFetch, getVouchers]);

  document.title = "Vouchers | Painel Fort Exchange";

  const handleTermoKeyDown = (e) => {
    if (e.key === "Enter") {
      setTermo(inputTermo);
      setPagina(1);
      setShouldFetch(true);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Vouchers"
            pageTitle={
              isLojista ? "Lojista" : isCliente ? "Clientes Fort" : "Aplicações"
            }
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center border-0">
                  <h5 className="card-title mb-0 flex-grow-1">
                    {cliente_id ? "Vouchers do Cliente" : "Vouchers Utilizados"}
                  </h5>
                </CardHeader>
                <CardBody>
                  {/* Campo de busca */}
                  <div className="mb-3">
                    <InputGroup>
                      <div className="input-group-text">
                        <FeatherIcon icon="search" className="icon-dual" />
                      </div>
                      <Input
                        type="text"
                        placeholder="Buscar por código, parceiro, campanha..."
                        value={inputTermo}
                        onChange={e => setInputTermo(e.target.value)}
                        onKeyDown={handleTermoKeyDown}
                      />
                    </InputGroup>
                  </div>
                  {loading ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <Spinner height={100} width={100} />
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Data de Uso</th>
                              <th>Código</th>
                              <th>Valor</th>
                              <th>Parceiro</th>
                              <th>Campanha</th>
                              <th>Validade</th>
                            </tr>
                          </thead>
                          <tbody>
                            {vouchers.length === 0 ? (
                              <tr>
                                <td colSpan={6} className="text-center text-muted py-4">
                                  Nenhum voucher encontrado.
                                </td>
                              </tr>
                            ) : (
                              vouchers.map((item, idx) => (
                                <tr key={item.id || idx}>
                                  {/* Data de Uso */}
                                  <td>
                                    <b>{moment(item.created_at).format("DD/MM/YYYY")} </b>
                                    <small className="text-muted">{moment(item.created_at).format("HH:mm")}</small>
                                  </td>
                                  {/* Código */}
                                  <td>
                                    <span className="badge badge-soft-primary">
                                      {item.codigo}
                                    </span>
                                  </td>
                                  {/* Valor */}
                                  <td>
                                    <b className="text-success">{formatCurrency(item.valor)}</b>
                                  </td>
                                  {/* Parceiro */}
                                  <td>
                                    {item.parceiro?.nome || "-"}
                                  </td>
                                  {/* Campanha */}
                                  <td>
                                    {item.campanha?.nome || "-"}
                                  </td>
                                  {/* Validade */}
                                  <td>
                                    {item.campanha?.validade ? (
                                      <>
                                        <b>{moment(item.campanha.validade).format("DD/MM/YYYY")}</b>
                                        {moment(item.campanha.validade).isBefore(moment()) && (
                                          <small className="text-danger d-block">Expirado</small>
                                        )}
                                      </>
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                      <AwesomePagination
                        pagina={pagina}
                        totalPaginas={totalPaginas}
                        setPagina={p => {
                          setPagina(p);
                          setShouldFetch(true);
                        }}
                      />
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Vouchers; 